import { useEffect, useState } from 'react';
import { RawGoal } from '@views';
import { EmptyFunction } from '@utils/common-types';
import { useCreateGoalAction } from '@actions/CreateGoalAction';
import { DateTime } from 'luxon';
import { GroupDateMapper } from '@app/utils/GroupDateMapper';
import { useAllCohortUsersViewCreator } from '@app/view-creator/active-participants-view-creator';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { CohortTimelineIndexZero } from '@app/enums/CohortTimeline';
import { useActivityLookupViewCreator } from '@app/view-creator';

export const useUpdateGoalsDialogController = (groupId: number, refresh: EmptyFunction, groupStartDate: DateTime) => {
  const {
    data: activityLookupData,
    isLoading: isActivityLookupLoading,
    refetch: refetchActivityLookup,
  } = useActivityLookupViewCreator();
  const [showNewGoalDialog, setShowNewGoalDialog] = useState<boolean>(false);
  const [rawGoals, setRawGoals] = useState<RawGoal[]>([]);
  const { mutateAsync: createGoals } = useCreateGoalAction();
  const saveGoals = async (_e: any, _reason: 'SAVE' | 'CANCEL', goals: RawGoal[]) => {
    try {
      await createGoals(goals);
    } catch (error: any) {
      // If the backend rejects (e.g., goals already exist or wrong period),
      // refresh to get fresh data and close the dialog
      console.warn('Goal creation rejected by server:', error?.message || error);
    }
    setShowNewGoalDialog(false);
    refresh();
  };
  const { data: userData } = useAllCohortUsersViewCreator(groupId);
  const cookies = useAppCookies();
  const currentUserId = cookies.profile.id;
  const currentUser = userData?.allActiveCohortUsers?.find((p) => p.CohortUser.userId === currentUserId);
  const environment = import.meta.env.VITE_APP_ENV || 'development';

  useEffect(() => {
    // If the user is a coach, close the dialog (no goal setting)
    if (currentUser?.CohortUser.role === 'Coach') {
      setShowNewGoalDialog(false);
      return;
    }

    // If the user is a captain or admin, and we're *not* on a development environment, close the dialog (no goal setting)
    if (
      (currentUser?.CohortUser.role === 'Captain' || currentUser?.CohortUser.user.role === 'Admin') &&
      !['development', 'dev', 'test', 'local', 'staging'].includes(environment)
    ) {
      setShowNewGoalDialog(false);
      return;
    }

    // Get the current cohort day number
    const dateMapper = new GroupDateMapper(groupStartDate);
    const date = DateTime.local().toISODate();
    const currentCohortDayNumber = dateMapper.day(date);

    // Automatically show the goal dialog during the exercise period.
    // NOTE: The goal tracker card also confirms the user has no goals for the current period
    // and the survey drawer is not open, so this won't show on top of the goal survey drawer
    const showGoalDialog =
      currentCohortDayNumber > CohortTimelineIndexZero.Week0Day1 &&
      currentCohortDayNumber <= CohortTimelineIndexZero.LastExerciseDay;
    setShowNewGoalDialog(showGoalDialog);
  }, [userData]);

  return {
    handleShowNewGoalDialog: () => setShowNewGoalDialog(true),
    showNewGoalDialog,
    submitGoals: saveGoals,
    rawGoals,
    setRawGoals,
    activityLookupData,
    isActivityLookupLoading,
    refetchActivityLookup,
  };
};
